import type {
    AdminV2RevenueAccessContextResponse,
    AdminV2RevenueDisputeDetailResponse,
    AdminV2RevenueDisputesResponse,
    AdminV2RevenueSubscriptionsResponse,
    AdminV2RevenueTransactionDetailResponse,
    AdminV2RevenueTransactionsResponse,
    AdminV2RevenueWebhookEventsResponse,
} from "@/types/adminV2";

import type { ApiEndpointBuilder } from "../baseApi";

export const buildAdminV2RevenueReadEndpoints = (
    builder: ApiEndpointBuilder,
) => ({
    getAdminV2RevenueAccessContext: builder.query<
        AdminV2RevenueAccessContextResponse,
        { organisationId?: string } | void
    >({
        query: params => ({
            url: "/admin/v2/revenue/access-context",
            params: params ?? undefined,
        }),
        providesTags: [{ type: "AdminV2RevenueAccess", id: "CONTEXT" }],
        keepUnusedDataFor: 20,
    }),

    getAdminV2RevenueTransactions: builder.query<
        AdminV2RevenueTransactionsResponse,
        {
            organisationId?: string;
            status?: string;
            limit?: number;
            offset?: number;
        } | void
    >({
        query: params => ({
            url: "/admin/v2/revenue/transactions",
            params: params ?? undefined,
        }),
        providesTags: [{ type: "AdminV2RevenueTransactions", id: "LIST" }],
        keepUnusedDataFor: 20,
    }),

    getAdminV2RevenueTransactionDetail: builder.query<
        AdminV2RevenueTransactionDetailResponse,
        { transactionId: string }
    >({
        query: ({ transactionId }) => ({
            url: `/admin/v2/revenue/transactions/${transactionId}`,
        }),
        providesTags: (_result, _error, { transactionId }) => [
            { type: "AdminV2RevenueTransactions", id: transactionId },
        ],
        keepUnusedDataFor: 10,
    }),

    getAdminV2RevenueSubscriptions: builder.query<
        AdminV2RevenueSubscriptionsResponse,
        {
            organisationId?: string;
            status?: string;
            limit?: number;
            offset?: number;
        } | void
    >({
        query: params => ({
            url: "/admin/v2/revenue/subscriptions",
            params: params ?? undefined,
        }),
        providesTags: [{ type: "AdminV2RevenueSubscriptions", id: "LIST" }],
        keepUnusedDataFor: 20,
    }),

    getAdminV2RevenueDisputes: builder.query<
        AdminV2RevenueDisputesResponse,
        {
            organisationId?: string;
            status?: string;
            limit?: number;
            offset?: number;
        } | void
    >({
        query: params => ({
            url: "/admin/v2/revenue/disputes",
            params: params ?? undefined,
        }),
        providesTags: [{ type: "AdminV2RevenueDisputes", id: "LIST" }],
        keepUnusedDataFor: 20,
    }),

    getAdminV2RevenueDisputeDetail: builder.query<
        AdminV2RevenueDisputeDetailResponse,
        { disputeId: string }
    >({
        query: ({ disputeId }) => ({
            url: `/admin/v2/revenue/disputes/${disputeId}`,
        }),
        providesTags: (_result, _error, { disputeId }) => [
            { type: "AdminV2RevenueDisputes", id: disputeId },
        ],
        keepUnusedDataFor: 10,
    }),

    getAdminV2RevenueWebhookEvents: builder.query<
        AdminV2RevenueWebhookEventsResponse,
        {
            organisationId?: string;
            status?: "queued" | "processing" | "processed" | "failed";
            eventType?: string;
            limit?: number;
            offset?: number;
        } | void
    >({
        query: params => ({
            url: "/admin/v2/revenue/webhooks",
            params: params ?? undefined,
        }),
        providesTags: [{ type: "AdminV2RevenueTransactions", id: "WEBHOOKS" }],
        keepUnusedDataFor: 10,
    }),

    getAdminV2RevenueWebhookEventDetail: builder.query<
        { event: AdminV2RevenueWebhookEventsResponse["events"][number] },
        { webhookEventId: string }
    >({
        query: ({ webhookEventId }) => ({
            url: `/admin/v2/revenue/webhooks/${webhookEventId}`,
        }),
        providesTags: (_result, _error, { webhookEventId }) => [
            {
                type: "AdminV2RevenueTransactions",
                id: `WEBHOOK:${webhookEventId}`,
            },
        ],
        keepUnusedDataFor: 10,
    }),
});
